import React, { useRef, useState, useEffect } from 'react';
import { Button, DatePicker, Form, Schema, SelectPicker } from 'rsuite';
import { ItemDataType } from 'rsuite/@types/common';
import RsModal from '@/widgets/components/rs-modal';
import RsModalBody from '@/widgets/components/rs-modal-body';
import RsModalFooter from '@/widgets/components/rs-modal-footer';
import RsModalHeader from '@/widgets/components/rs-modal-header';
import styled from 'styled-components';

const StyledRsModalHeader = styled(RsModalHeader)`
  font-size: 20px;
`;

const StyledRsModalBody = styled(RsModalBody)`
  padding: 0;
  margin: 20px 0;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 16px;
`;

const { StringType, NumberType, DateType } = Schema.Types;
const REQUIRED_FIELD = 'This field is required.';
const VALID_EMAIL_ERR = 'Please enter a valid email address.';
const VALID_PHONE_ERR = 'Please enter a valid telephone number.';
const INVALID_DATE_MIN = 'Inspection Date should be later than today.';

const currentDate = new Date();
currentDate.setDate(currentDate.getDate() + 1);

const model = Schema.Model<BookInspectionFormValues>({
  propertyType: StringType().isRequired(REQUIRED_FIELD),
  inspectionType: StringType().isRequired(REQUIRED_FIELD),
  inspectionTime: DateType().min(currentDate, INVALID_DATE_MIN),
  contactPersonName: StringType().isRequired(REQUIRED_FIELD).maxLength(50),
  contactPersonPhone: StringType().isRequired(REQUIRED_FIELD),
  contactPersonEmail: StringType()
    .isEmail(VALID_EMAIL_ERR)
    .isRequired(REQUIRED_FIELD),
  contactPersonRole: StringType().isRequired(REQUIRED_FIELD),
});

interface BookInspectionsModalProps {
  open: boolean;
  onClose: () => void;
  property: any;
  onSubmitInspection: (data: BookInspectionFormValues) => void;
  propertyTypes: ItemDataType[];
  inspectionTypes: ItemDataType[];
  roles: ItemDataType[];
  isLoading: boolean;
}

export interface BookInspectionFormValues {
  propertyType?: string;
  inspectionType?: string;
  inspectionTime: string;
  contactPersonName: string;
  contactPersonPhone: string;
  contactPersonEmail: string;
  contactPersonRole: string;
}

const INITIAL_VALUES: BookInspectionFormValues = {
  propertyType: undefined,
  inspectionType: undefined,
  inspectionTime: '',
  contactPersonName: '',
  contactPersonPhone: '',
  contactPersonEmail: '',
  contactPersonRole: '',
};

export function BookInspectionsModal({
  open,
  property,
  onClose,
  onSubmitInspection,
  propertyTypes,
  inspectionTypes,
  roles,
  isLoading,
}: BookInspectionsModalProps) {
  const formRef = useRef(null);
  const [formValue, setFormValue] =
    useState<BookInspectionFormValues>(INITIAL_VALUES);

  const [formError, setFormError] = useState();
  const handleSubmit = () => {
    if (!formRef.current?.check()) {
      return;
    }

    onSubmitInspection(formValue);
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
      resetForm();
    }
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setFormValue(INITIAL_VALUES);
  };

  return (
    <RsModal open={open} onClose={handleClose}>
      <StyledRsModalHeader>
        Book an inspection for {property.address}
      </StyledRsModalHeader>

      <StyledRsModalBody>
        <Form
          fluid
          onChange={setFormValue}
          formValue={formValue}
          model={model}
          ref={formRef}
          disabled={isLoading}
          format="MM-dd-yyyy"
        >
          <Form.Group controlId="inspectionTime">
            <Form.ControlLabel>Inspection Date</Form.ControlLabel>
            <Form.Control
              accepter={DatePicker}
              block
              placeholder="Inspection Date"
              name="inspectionTime"
            />
          </Form.Group>
          <Form.Group controlId="propertyType">
            <Form.ControlLabel>Property Type</Form.ControlLabel>
            <Form.Control
              accepter={SelectPicker}
              block
              data={propertyTypes}
              searchable={false}
              placeholder="Property Type"
              name="propertyType"
            />
          </Form.Group>
          <Form.Group controlId="inspectionType">
            <Form.ControlLabel>Inspection Type</Form.ControlLabel>
            <Form.Control
              accepter={SelectPicker}
              block
              data={inspectionTypes}
              searchable={false}
              placeholder="Inspection Type"
              name="inspectionType"
            />
          </Form.Group>
          <Form.Group controlId="contactPersonName">
            <Form.ControlLabel>Owner/Agent Name</Form.ControlLabel>
            <Form.Control name="contactPersonName" autoComplete="off" />
          </Form.Group>
          <Form.Group controlId="contactPersonRole">
            <Form.ControlLabel>Role</Form.ControlLabel>
            <Form.Control
              accepter={SelectPicker}
              block
              data={roles}
              searchable={false}
              placeholder="Role"
              name="contactPersonRole"
            />
          </Form.Group>
          <Form.Group controlId="contactPersonPhone">
            <Form.ControlLabel>Phone Number</Form.ControlLabel>
            <Form.Control
              placeholder="Phone Number"
              name="contactPersonPhone"
            />
          </Form.Group>
          <Form.Group controlId="contactPersonEmail">
            <Form.ControlLabel>Email</Form.ControlLabel>
            <Form.Control placeholder="Email" name="contactPersonEmail" />
          </Form.Group>
        </Form>
      </StyledRsModalBody>
      <RsModalFooter>
        <ButtonsWrapper>
          <Button
            onClick={handleSubmit}
            appearance="primary"
            loading={isLoading}
            disabled={isLoading}
          >
            Send
          </Button>
          <Button
            appearance="ghost"
            onClick={handleClose}
            disabled={isLoading}
            loading={isLoading}
          >
            Close
          </Button>
        </ButtonsWrapper>
      </RsModalFooter>
    </RsModal>
  );
}
